import React, { useState } from 'react';
import Reveal from './Reveal';
import { COMPANY, SOCIAL, API } from '../config';

const CONTACT_INFO = [
  { icon: '📍', label: 'Office', value: 'Bhoganhalli Road, Bangalore', href: null },
  { icon: '📞', label: 'Phone', value: COMPANY.PHONE, href: `tel:${COMPANY.PHONE.replace(/\s+/g, '')}` },
  { icon: '✉️', label: 'Email', value: COMPANY.EMAIL, href: `mailto:${COMPANY.EMAIL}` },
  { icon: '🕒', label: 'Hours', value: 'Mon–Fri: 9AM – 6PM IST', href: null },
];

const PROJECT_TYPES = [
  { value: '', label: 'Select a service' },
  { value: 'ai-development', label: 'AI Development' },
  { value: 'web-development', label: 'Web Development' },
  { value: 'mobile-development', label: 'Mobile Development' },
  { value: 'custom-software', label: 'Custom Software' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'training', label: 'Training' },
  { value: 'other', label: 'Other' },
];

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', project: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, type: '', message: '' });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');

  const showToast = (type, message) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast(t => ({ ...t, open: false })), 6000);
  };

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const projectLabel = PROJECT_TYPES.find(type => type.value === form.project)?.label || 'Not selected';
    const requirement = [
      'Avarnix Technology requirement',
      '',
      `Full Name: ${form.name}`,
      `Email Address: ${form.email}`,
      `Phone: ${form.phone || 'Not provided'}`,
      `Project Type: ${projectLabel}`,
      '',
      'Project Description:',
      form.message,
    ].join('\n');

    try {
      const res = await fetch(`${API.BASE_URL}/api/v1/bulk/requirement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || 'Not provided',
          requirement,
          product_name: 'Avarnix Technology',
        }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast('success', 'Message sent! We\'ll get back to you within 24 hours.');
        setForm({ name: '', email: '', phone: '', project: '', message: '' });
      } else throw new Error(data?.message || `Error ${res.status}`);
    } catch (err) {
      showToast('error', err.message || 'Submission failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setNewsletterStatus('loading');
    try {
      const res = await fetch(`${API.BASE_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail, isSubscribed: 1 }),
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setNewsletterStatus('success');
        setNewsletterEmail('');
        showToast('success', data.message || 'Subscribed successfully!');
      } else throw new Error(data?.message || 'Subscription failed');
    } catch (err) {
      setNewsletterStatus('error');
      showToast('error', err.message || 'Subscription failed');
    } finally { setTimeout(() => setNewsletterStatus(''), 4000); }
  };

  return (
    <section id="contact" className="section-padding bg-brand-surface relative overflow-hidden scroll-mt-16">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-blue/5 blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <Reveal from="up" className="section-header">
          <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-sm font-medium text-brand-blue border border-brand-blue/20 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-blue animate-pulse" />
            Let's Connect
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-brand-text mb-5">
            Get In <span className="text-gradient">Touch</span>
          </h2>
          <p className="text-lg text-brand-muted max-w-2xl mx-auto">
            Ready to build something great? Tell us about your project and we'll get back to you within 24 hours.
          </p>
        </Reveal>

        {/* Toast */}
        {toast.open && (
          <div className={`mb-6 p-4 rounded-xl text-sm font-medium flex items-center gap-3 border transition-all ${
            toast.type === 'success'
              ? 'bg-brand-green/10 border-brand-green/30 text-brand-green'
              : 'bg-red-500/10 border-red-500/30 text-red-400'
          }`}>
            <span>{toast.type === 'success' ? '✅' : '⚠️'}</span>
            {toast.message}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-10">
          {/* Form */}
          <div className="xl:col-span-2">
            <Reveal from="left">
              <div className="card p-8">
                <h3 className="text-xl font-bold text-brand-text mb-6">Send Us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-brand-muted mb-2">Full Name *</label>
                      <input type="text" name="name" value={form.name} onChange={handleChange} required
                        placeholder="John Doe"
                        className="w-full px-4 py-3 bg-brand-bg border border-brand-border rounded-xl text-brand-text placeholder-brand-dim focus:outline-none focus:ring-2 focus:ring-brand-blue/40 focus:border-brand-blue/40 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-brand-muted mb-2">Email Address *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} required
                        placeholder="john@company.com"
                        className="w-full px-4 py-3 bg-brand-bg border border-brand-border rounded-xl text-brand-text placeholder-brand-dim focus:outline-none focus:ring-2 focus:ring-brand-blue/40 focus:border-brand-blue/40 transition-all" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-brand-muted mb-2">Phone</label>
                      <input type="text" name="phone" value={form.phone} onChange={handleChange}
                        placeholder="+91 98765 43210"
                        className="w-full px-4 py-3 bg-brand-bg border border-brand-border rounded-xl text-brand-text placeholder-brand-dim focus:outline-none focus:ring-2 focus:ring-brand-blue/40 focus:border-brand-blue/40 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-brand-muted mb-2">Project Type</label>
                      <select name="project" value={form.project} onChange={handleChange}
                        className="w-full px-4 py-3 bg-brand-bg border border-brand-border rounded-xl text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-blue/40 focus:border-brand-blue/40 transition-all">
                        {PROJECT_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-muted mb-2">Project Description *</label>
                    <textarea name="message" value={form.message} onChange={handleChange} required rows={5}
                      placeholder="Tell us about your project requirements, goals, and timeline..."
                      className="w-full px-4 py-3 bg-brand-bg border border-brand-border rounded-xl text-brand-text placeholder-brand-dim focus:outline-none focus:ring-2 focus:ring-brand-blue/40 focus:border-brand-blue/40 transition-all resize-none" />
                  </div>
                  <button type="submit" disabled={submitting} className="w-full btn-primary py-4 text-base disabled:opacity-50 disabled:cursor-not-allowed">
                    {submitting ? (
                      <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Sending...</>
                    ) : (
                      <><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>Send Message</>
                    )}
                  </button>
                </form>

                {/* Newsletter */}
                <div className="hidden mt-8 pt-6 border-t border-brand-border">
                  <h4 className="text-brand-text font-bold mb-1">📮 Stay in the Loop</h4>
                  <p className="text-brand-muted text-xs mb-4">Product updates & AI insights, delivered to your inbox.</p>
                  <form onSubmit={handleNewsletter} className="flex gap-3">
                    <input type="email" value={newsletterEmail} onChange={e => setNewsletterEmail(e.target.value)} required
                      placeholder="you@company.com"
                      className="flex-1 px-4 py-2.5 bg-brand-bg border border-brand-border rounded-xl text-brand-text placeholder-brand-dim text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/40 transition-all" />
                    <button type="submit" disabled={newsletterStatus === 'loading'}
                      className="btn-primary text-sm py-2.5 px-5 disabled:opacity-50">
                      {newsletterStatus === 'loading' ? 'Subscribing…' : 'Subscribe'}
                    </button>
                  </form>
                  {newsletterStatus === 'success' && <p className="text-brand-green text-xs mt-2 font-medium">✅ Subscribed! Watch your inbox.</p>}
                </div>
              </div>
            </Reveal>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            <Reveal from="right">
              <div className="card p-6">
                <h3 className="text-brand-text font-bold mb-5">Contact Details</h3>
                <div className="space-y-4">
                  {CONTACT_INFO.map((c, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-brand-blue/15 rounded-lg flex items-center justify-center flex-shrink-0 text-base">{c.icon}</div>
                      <div>
                        <div className="text-brand-dim text-xs font-semibold uppercase tracking-wider mb-0.5">{c.label}</div>
                        {c.href
                          ? <a href={c.href} className="text-brand-muted text-sm hover:text-brand-blue transition-colors font-medium">{c.value}</a>
                          : <div className="text-brand-muted text-sm font-medium">{c.value}</div>
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal from="right" delay={80}>
              <div className="card p-6">
                <h3 className="text-brand-text font-bold mb-4">Follow Us</h3>
                <div className="space-y-3">
                  {[
                    { name: 'LinkedIn', icon: '💼', url: SOCIAL.LINKEDIN },
                    { name: 'Twitter / X', icon: '🐦', url: SOCIAL.TWITTER },
                    { name: 'GitHub', icon: '⚡', url: SOCIAL.GITHUB },
                  ].filter(s => s.url && s.url !== '#').length === 0
                    ? <p className="text-brand-dim text-sm italic">Social profiles coming soon</p>
                    : [
                        { name: 'LinkedIn', icon: '💼', url: SOCIAL.LINKEDIN },
                        { name: 'Twitter / X', icon: '🐦', url: SOCIAL.TWITTER },
                        { name: 'GitHub', icon: '⚡', url: SOCIAL.GITHUB },
                      ].filter(s => s.url && s.url !== '#').map((s, i) => (
                        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-brand-bg rounded-xl border border-brand-border hover:border-brand-blue/30 hover:text-brand-blue text-brand-muted text-sm font-medium transition-all duration-200">
                          <span className="text-lg">{s.icon}</span>{s.name}
                          <svg className="w-3.5 h-3.5 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                        </a>
                      ))
                  }
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Map */}
        <Reveal from="up">
          <div className="card p-0 overflow-hidden">
            <div className="p-6 pb-0 flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-blue/15 rounded-xl flex items-center justify-center text-lg">📍</div>
              <h3 className="text-brand-text font-bold text-lg">Our Location — Bangalore, India</h3>
            </div>
            <div className="p-6">
              <div className="rounded-xl overflow-hidden border border-brand-border">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1654.2226297085026!2d77.69874463771292!3d12.92815208891544!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1375e83d5831%3A0xff007d401e6d5c7f!2sRohan%20Iksha!5e0!3m2!1sen!2sin!4v1760963920893!5m2!1sen!2sin"
                  width="100%" height="340" style={{ border: 0 }} allowFullScreen="" loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade" title="Avarnix Technologies Location"
                />
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
